// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack API Hooks — Dashboard, Sessions, Contributors, Decisions
// apps/web/src/lib/api/hooks-extended.ts
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type {
  DashboardData,
  Session,
  CreateSessionRequest,
  Contributor,
  CreateContributorRequest,
  Decision,
  CreateDecisionRequest,
} from './types-extended';
import type { ApiError } from './types';

// ─── Generic fetch hook ─────────────────────────────────────────────────────

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
}

function useApiFetch<T>(path: string, enabled = true): UseApiState<T> {
  const { createApi } = useApiClient();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api = await createApi();
      const response = await api.get<T>(path);
      setData(response.data);
    } catch (err: any) {
      setError({
        message: err.response?.data?.message || err.message || 'Request failed',
        status: err.response?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  }, [createApi, path]);

  useEffect(() => {
    if (enabled) fetch();
  }, [fetch, enabled]);

  return { data, loading, error, refetch: fetch };
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function useDashboard() {
  return useApiFetch<DashboardData>('/api/v1/dashboard');
}

// ─── Sessions ───────────────────────────────────────────────────────────────

export function useSessions(filters?: {
  projectId?: number;
  contributorId?: number;
  workItemId?: number;
}) {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set('projectId', String(filters.projectId));
  if (filters?.contributorId) params.set('contributorId', String(filters.contributorId));
  if (filters?.workItemId) params.set('workItemId', String(filters.workItemId));
  const qs = params.toString();
  return useApiFetch<Session[]>(`/api/v1/sessions${qs ? `?${qs}` : ''}`);
}

export function useSessionMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(
    async (data: CreateSessionRequest): Promise<Session | null> => {
      try {
        setLoading(true);
        setError(null);
        const api = await createApi();
        const response = await api.post<Session>('/api/v1/sessions', data);
        return response.data;
      } catch (err: any) {
        setError({ message: err.response?.data?.message || 'Failed to create session', status: err.response?.status || 500 });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]
  );

  const update = useCallback(
    async (id: number, data: Partial<CreateSessionRequest>): Promise<Session | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.put<Session>(`/api/v1/sessions/${id}`, data);
        return response.data;
      } catch { return null; }
      finally { setLoading(false); }
    },
    [createApi]
  );

  return { create, update, loading, error };
}

// ─── Contributors ───────────────────────────────────────────────────────────

export function useContributors() {
  return useApiFetch<Contributor[]>('/api/v1/contributors');
}

export function useContributorMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(
    async (data: CreateContributorRequest): Promise<Contributor | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.post<Contributor>('/api/v1/contributors', data);
        return response.data;
      } catch { return null; }
      finally { setLoading(false); }
    },
    [createApi]
  );

  const update = useCallback(
    async (id: number, data: Partial<CreateContributorRequest>): Promise<Contributor | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.put<Contributor>(`/api/v1/contributors/${id}`, data);
        return response.data;
      } catch { return null; }
      finally { setLoading(false); }
    },
    [createApi]
  );

  return { create, update, loading };
}

// ─── Decisions ──────────────────────────────────────────────────────────────

export function useDecisions(filters?: {
  workItemId?: number;
  minImpactScope?: number;
  search?: string;
}) {
  const params = new URLSearchParams();
  if (filters?.workItemId) params.set('workItemId', String(filters.workItemId));
  if (filters?.minImpactScope) params.set('minImpactScope', String(filters.minImpactScope));
  if (filters?.search) params.set('search', filters.search);
  const qs = params.toString();
  return useApiFetch<Decision[]>(`/api/v1/decisions${qs ? `?${qs}` : ''}`);
}

export function useDecisionMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(
    async (data: CreateDecisionRequest): Promise<Decision | null> => {
      try {
        setLoading(true);
        const api = await createApi();
        const response = await api.post<Decision>('/api/v1/decisions', data);
        return response.data;
      } catch { return null; }
      finally { setLoading(false); }
    },
    [createApi]
  );

  return { create, loading };
}

// ─── Technical Uncertainty ──────────────────────────────────────────────────

export interface TechnicalUncertainty {
  workItemId: number;
  workItemTitle: string;
  projectId: number;
  projectName: string;
  uncertaintyScore: number | null;
  uncertaintyRationale: string | null;
  cpaReviewed: boolean;
  cpaReviewedAt: string | null;
  cpaReviewedBy: string | null;
}

export function useTechnicalUncertainty(projectId: number | null) {
  return useApiFetch<TechnicalUncertainty[]>(
    `/api/v1/technical-uncertainty/project/${projectId}`,
    projectId !== null
  );
}

export function useTechnicalUncertaintyMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const upsert = useCallback(async (data: { workItemId: number; uncertaintyScore: number; uncertaintyRationale: string }) => {
    try {
      setLoading(true);
      const api = await createApi();
      await api.put('/api/v1/technical-uncertainty', data);
      return true;
    } catch { return false; }
    finally { setLoading(false); }
  }, [createApi]);

  const markCpaReviewed = useCallback(async (workItemId: number) => {
    try {
      setLoading(true);
      const api = await createApi();
      await api.post(`/api/v1/technical-uncertainty/${workItemId}/cpa-review`);
      return true;
    } catch { return false; }
    finally { setLoading(false); }
  }, [createApi]);

  return { upsert, markCpaReviewed, loading };
}

// ─── AI Reports ─────────────────────────────────────────────────────────────

// List endpoint shape — what GET /api/v1/ai-reports returns. No
// narrative/tokens/cost (those live on the detail endpoint to avoid
// shipping the full markdown body in the list).
export interface AIReport {
  id: number;
  projectId: number;
  projectName: string;
  reportType: string;
  title: string;
  status: string;
  generatedDate: string;
}

// Detail endpoint shape — what GET /api/v1/ai-reports/{id} returns.
// Includes the narrative + cost telemetry. Status flips from
// "Generating" → "Ready"/"Failed" once the Hangfire job completes.
export interface AIReportDetail {
  reportId: number;
  projectId: number;
  projectName: string;
  reportType: string;
  title: string;
  narrative: string;
  executiveSummary: string | null;
  sections: { title: string; content: string }[] | null;
  tokensIn: number;
  tokensOut: number;
  costUSD: number;
  model: string;
  generatedDate: string;
}

export function useAIReports(projectId: number | null) {
  return useApiFetch<AIReport[]>(
    `/api/v1/ai-reports?projectId=${projectId}`,
    projectId !== null
  );
}

export function useAIReportMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async (projectId: number, reportType: string = 'RDCreditNarrative') => {
    try {
      setLoading(true);
      const api = await createApi();
      const res = await api.post<AIReport>('/api/v1/ai-reports/generate', { projectId, reportType });
      return res.data;
    } catch { return null; }
    finally { setLoading(false); }
  }, [createApi]);

  // Fetches the full detail (narrative + tokens + cost) for one report.
  // Used when the user clicks a list row to view it — kept out of the
  // list payload so the list response stays small.
  const fetchDetail = useCallback(async (reportId: number): Promise<AIReportDetail | null> => {
    try {
      const api = await createApi();
      const res = await api.get<AIReportDetail>(`/api/v1/ai-reports/${reportId}`);
      return res.data;
    } catch { return null; }
  }, [createApi]);

  return { generate, fetchDetail, loading };
}

// ─── Import Classification ──────────────────────────────────────────────────

export function useClassifyMutation() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const classify = useCallback(async (batchId: string) => {
    try {
      setLoading(true);
      const api = await createApi();
      const res = await api.post<{ classified: number; failed: number; totalCostUSD: number; totalRows: number }>(
        '/api/v1/import/classify', { batchId });
      return res.data;
    } catch { return null; }
    finally { setLoading(false); }
  }, [createApi]);

  return { classify, loading };
}

// ─── Projects (for selectors) ───────────────────────────────────────────────

export function useProjectList() {
  return useApiFetch<Array<{ id: number; name: string; status: string }>>('/api/v1/projects');
}

// ─── Phases ─────────────────────────────────────────────────────────────────

export interface Phase {
  id: number;
  projectId: number;
  name: string;
  description: string | null;
  sortOrder: number;
  status: string;
  startDate: string | null;
  endDate: string | null;
}

export function usePhases(projectId: number | null) {
  return useApiFetch<Phase[]>(`/api/v1/phases/by-project/${projectId}`, projectId !== null);
}

export function usePhaseMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(async (data: { projectId: number; name: string; description?: string; sortOrder?: number }) => {
    try { setLoading(true); const api = await createApi(); const r = await api.post<Phase>('/api/v1/phases', data); return r.data; }
    catch { return null; } finally { setLoading(false); }
  }, [createApi]);

  const update = useCallback(async (id: number, data: Partial<Phase>) => {
    try { setLoading(true); const api = await createApi(); await api.put(`/api/v1/phases/${id}`, data); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  const remove = useCallback(async (id: number) => {
    try { setLoading(true); const api = await createApi(); await api.delete(`/api/v1/phases/${id}`); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  return { create, update, remove, loading };
}

// ─── Milestones ─────────────────────────────────────────────────────────────

export interface Milestone {
  id: number;
  phaseId: number;
  name: string;
  description: string | null;
  targetDate: string | null;
  completedDate: string | null;
  status: string;
}

export function useMilestones(phaseId: number | null) {
  return useApiFetch<Milestone[]>(`/api/v1/milestones/by-phase/${phaseId}`, phaseId !== null);
}

export function useMilestoneMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(async (data: { phaseId: number; name: string; description?: string; targetDate?: string }) => {
    try { setLoading(true); const api = await createApi(); const r = await api.post<Milestone>('/api/v1/milestones', data); return r.data; }
    catch { return null; } finally { setLoading(false); }
  }, [createApi]);

  const update = useCallback(async (id: number, data: Partial<Milestone>) => {
    try { setLoading(true); const api = await createApi(); await api.put(`/api/v1/milestones/${id}`, data); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  const remove = useCallback(async (id: number) => {
    try { setLoading(true); const api = await createApi(); await api.delete(`/api/v1/milestones/${id}`); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  return { create, update, remove, loading };
}

// ─── Effort Override + Recalc ───────────────────────────────────────────────

export function useEffortMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const setWeeks = useCallback(async (workItemId: number, weeks: number) => {
    try { setLoading(true); const api = await createApi(); await api.put(`/api/v1/effort/set-weeks/${workItemId}`, { equivalentWeeks: weeks }); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  const recalcProject = useCallback(async (projectId: number) => {
    try { setLoading(true); const api = await createApi(); await api.post(`/api/v1/effort/recalc/${projectId}`); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  return { setWeeks, recalcProject, loading };
}

// ─── Work Items (global search) ─────────────────────────────────────────────

export interface WorkItemSearchResult {
  id: number;
  projectId: number;
  projectName: string;
  phaseId: number | null;
  phaseName: string | null;
  title: string;
  status: string;
  complexityScore: number | null;
  equivalentWeeks: number | null;
}

export function useWorkItems(filters?: { search?: string; projectId?: number; page?: number; pageSize?: number }) {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.projectId) params.set('projectId', filters.projectId.toString());
  if (filters?.page) params.set('page', filters.page.toString());
  if (filters?.pageSize) params.set('pageSize', filters.pageSize.toString());
  const qs = params.toString();
  return useApiFetch<WorkItemSearchResult[]>(`/api/v1/work-items${qs ? `?${qs}` : ''}`);
}

// ─── Artifacts ──────────────────────────────────────────────────────────────

export interface Artifact {
  id: number;
  sessionId: number;
  name: string;
  artifactType: string;
  contentPreview: string | null;
  createdAt: string;
}

export function useArtifacts(sessionId: number | null) {
  return useApiFetch<Artifact[]>(`/api/v1/artifacts/by-session/${sessionId}`, sessionId !== null);
}

export function useArtifactMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(async (data: { sessionId: number; name: string; artifactType: string; content?: string }) => {
    try { setLoading(true); const api = await createApi(); const r = await api.post<Artifact>('/api/v1/artifacts', data); return r.data; }
    catch { return null; } finally { setLoading(false); }
  }, [createApi]);

  const remove = useCallback(async (id: number) => {
    try { setLoading(true); const api = await createApi(); await api.delete(`/api/v1/artifacts/${id}`); return true; }
    catch { return false; } finally { setLoading(false); }
  }, [createApi]);

  return { create, remove, loading };
}
